import { useMemo } from 'react';
import { ISwapFailureRateReportRow } from '@nkr/models/index';
import { RccapStatus } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

interface IProps {
    list: ISwapFailureRateReportRow[],
    onGridReady: (e: GridReadyEvent) => void,
    onRespond: (id: number) => void,
    onView: (id: number) => void
}

const RccapGrid = (props: IProps) => {
    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortedChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    }

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            width: 55,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'swapNumber',
            headerName: 'Swap #',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const data = params.data as ISwapFailureRateReportRow;
                return <>{data.swapChainNumber ?? ''}/{data.swapClusterNumber ?? ''}/{data.swapPositionNumber ?? ''}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                const data = params.data as ISwapFailureRateReportRow;
                return `${data.swapChainNumber ?? ''}/${data.swapClusterNumber ?? ''}/${data.swapPositionNumber ?? ''}`;
            }
        },
        {
            colId: 'donorCenterAlias',
            headerName: 'Donor Center',
            field: 'donorCenterAlias',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Center</SortableHeaderComponent>,
        },
        {
            colId: 'donorAlias',
            headerName: 'Donor Alias',
            field: 'donorAlias',
            width: 105,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Alias</SortableHeaderComponent>,
        },
        {
            colId: 'recipientCenterAlias',
            headerName: 'Recipient Center',
            field: 'recipientCenterAlias',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Center</SortableHeaderComponent>,
        },
        {
            colId: 'recipientAlias',
            headerName: 'Recipient Alias',
            field: 'recipientAlias',
            width: 115,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Alias</SortableHeaderComponent>,
        },
        {
            colId: 'outcome',
            headerName: 'Outcome',
            field: 'outcome',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Outcome</SortableHeaderComponent>,
        },
        {
            colId: 'outcomeDate',
            headerName: 'Outcome Date',
            field: 'outcomeDate',
            width: 110,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Outcome<br />Date</SortableHeaderComponent>,
        },
        {
            colId: 'buttons',
            width: 105,
            sortable: false,
            cellClass: 'edit-cell',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}></SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const data = params.data as ISwapFailureRateReportRow;

                if (data.outcome != 'Failed' || data.rccapId == null) {
                    return null;
                }

                const canRespond = data.currentStatusId == RccapStatus.PendingResponse
                    || data.currentStatusId == RccapStatus.NeedsMoreInfo;

                return (
                    <div style={{ width: '100%' }}>
                        {canRespond && (
                            <Button block bsStyle="primary" bsSize="xs" onClick={() => props.onRespond(data.rccapId)}>Respond</Button>
                        )}

                        {canRespond == false && (
                            <Button block bsStyle="primary" bsSize="xs" onClick={() => props.onView(data.rccapId)}>View RCCAP</Button>
                        )}
                    </div>
                );
            }
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                enableCellTextSelection={true}
                enableRangeSelection={true}
                headerHeight={42}
                noRowsOverlayComponentFramework={() =>
                    <div>No report data found...</div>}
                suppressCellSelection={true}
                suppressContextMenu={true}
                onGridReady={props.onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortedChanged}
                onRowDataChanged={onRowDataChanged}
            />
        </GridWrapper>
    )
}

export default RccapGrid;